/**
 * Maîtres d'apprentissage d'une entreprise, sur sa fiche (ticket du
 * 13/10/2026) : ceux qu'elle a déjà, rappelés, et autant de nouveaux que
 * nécessaire, ajoutés par « + ».
 *
 * Ce sont les fiches que le formulaire du contrat propose ensuite au choix :
 * les saisir ici évite de les découvrir au moment du CERFA. Seule l'identité
 * est exigée ; le reste du bloc du CERFA se complète depuis le contrat.
 */

import { Plus, Trash2, UserRound } from 'lucide-react';
import { CLASSE_CHAMP } from '../../lib/fiche-besoin';
import { ligneMaitreVide, type LigneMaitre } from '../../lib/maitres-apprentissage';
import type { MaitreApprentissageLigne } from '../../lib/useEntreprises';

/** Au-delà, l'API refuse la saisie : le bouton s'arrête avant. */
const MAXIMUM_LIGNES = 20;

export function SaisieMaitresApprentissage({
  existants,
  lignes,
  onChange,
}: {
  existants: readonly MaitreApprentissageLigne[];
  lignes: LigneMaitre[];
  onChange: (lignes: LigneMaitre[]) => void;
}): JSX.Element {
  const modifier = (cle: string, champ: keyof Omit<LigneMaitre, 'cle'>, valeur: string): void =>
    onChange(lignes.map((ligne) => (ligne.cle === cle ? { ...ligne, [champ]: valeur } : ligne)));

  return (
    <div className="min-w-0 space-y-3 md:col-span-2">
      {existants.length > 0 && (
        <ul className="space-y-2" aria-label="Maîtres d’apprentissage mémorisés">
          {existants.map((maitre) => (
            <li
              key={maitre.id}
              className="flex min-w-0 items-start gap-3 rounded-xl border border-gray-100 bg-codialis-sand/60 px-3 py-2.5"
            >
              <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
              <div className="min-w-0 text-sm">
                <p className="font-bold text-codialis-navy">
                  {maitre.prenom} {maitre.nom}
                </p>
                <p className="break-words text-xs text-codialis-gray">
                  {[maitre.emploiOccupe, maitre.telephone, maitre.email]
                    .filter(Boolean)
                    .join(' · ') || 'Coordonnées à compléter depuis le contrat'}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {lignes.map((ligne, rang) => {
        const id = (champ: string): string => `${ligne.cle}-${champ}`;
        const numero = existants.length + rang + 1;
        return (
          <div key={ligne.cle} className="space-y-3 rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-codialis-gray">
                Maître d’apprentissage n°{numero}
              </p>
              <button
                type="button"
                onClick={() => onChange(lignes.filter((autre) => autre.cle !== ligne.cle))}
                aria-label={`Retirer le maître d’apprentissage n°${numero}`}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-gray-200 text-codialis-gray transition hover:bg-gray-50"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChampMaitre id={id('nom')} libelle="Nom de naissance">
                <input
                  id={id('nom')}
                  value={ligne.nom}
                  onChange={(evenement) => modifier(ligne.cle, 'nom', evenement.target.value)}
                  maxLength={100}
                  autoComplete="off"
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
              <ChampMaitre id={id('prenom')} libelle="Prénom">
                <input
                  id={id('prenom')}
                  value={ligne.prenom}
                  onChange={(evenement) => modifier(ligne.cle, 'prenom', evenement.target.value)}
                  maxLength={100}
                  autoComplete="off"
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
              <ChampMaitre id={id('emploi')} libelle="Emploi occupé" facultatif>
                <input
                  id={id('emploi')}
                  value={ligne.emploiOccupe}
                  onChange={(evenement) =>
                    modifier(ligne.cle, 'emploiOccupe', evenement.target.value)
                  }
                  maxLength={200}
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
              <ChampMaitre id={id('experience')} libelle="Années d’expérience" facultatif>
                <input
                  id={id('experience')}
                  inputMode="numeric"
                  value={ligne.anneesExperience}
                  onChange={(evenement) =>
                    modifier(
                      ligne.cle,
                      'anneesExperience',
                      evenement.target.value.replace(/\D/g, '').slice(0, 2),
                    )
                  }
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
              <ChampMaitre id={id('telephone')} libelle="Téléphone" facultatif>
                <input
                  id={id('telephone')}
                  type="tel"
                  inputMode="tel"
                  value={ligne.telephone}
                  onChange={(evenement) => modifier(ligne.cle, 'telephone', evenement.target.value)}
                  maxLength={30}
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
              <ChampMaitre id={id('email')} libelle="Courriel" facultatif>
                <input
                  id={id('email')}
                  type="email"
                  value={ligne.email}
                  onChange={(evenement) => modifier(ligne.cle, 'email', evenement.target.value)}
                  maxLength={200}
                  className={CLASSE_CHAMP}
                />
              </ChampMaitre>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => onChange([...lignes, ligneMaitreVide()])}
        disabled={lignes.length >= MAXIMUM_LIGNES}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-sm font-bold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-40"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Ajouter un maître d’apprentissage
      </button>
    </div>
  );
}

function ChampMaitre({
  id,
  libelle,
  facultatif = false,
  children,
}: {
  id: string;
  libelle: string;
  facultatif?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
      >
        {libelle}
        {facultatif && (
          <>
            {' '}
            <span className="font-normal normal-case tracking-normal text-codialis-gray">
              facultatif
            </span>
          </>
        )}
      </label>
      {children}
    </div>
  );
}
